/**
 * Imagem da viagem (teste físico, item 22) — cliente.
 *
 * Mesmo ciclo da foto do avatar: reduzida e recortada no aparelho, enviada
 * por URL assinada, confirmada. A URL de leitura é assinada e curta: quem
 * mostra pede por `useCapaDaViagem`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { enviarVariante } from '../memorias/api';

async function pedir<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: 'same-origin', ...init, headers: { ...(init?.body ? { 'content-type': 'application/json' } : {}), ...(init?.headers ?? {}) } });
  if (!res.ok) {
    const corpo = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(corpo.message ?? `Falha (${res.status})`);
  }
  return (await res.json()) as T;
}

export function useCapaDaViagem(tripId: string, temCapa = true) {
  return useQuery({ queryKey: ['viagem', 'capa', tripId], queryFn: () => pedir<{ url: string | null }>(`/api/v1/trips/${tripId}/cover`), enabled: Boolean(tripId) && temCapa, staleTime: 5 * 60_000 });
}

/** Sobe o recorte (JPEG quadrado) como imagem da viagem. */
export async function enviarCapa(tripId: string, recorte: Blob): Promise<{ url: string }> {
  const pedido = await pedir<{ key: string; upload: { url: string; method: 'PUT'; headers: Record<string, string> } }>(`/api/v1/trips/${tripId}/cover`, { method: 'POST', body: JSON.stringify({ mimeType: 'image/jpeg', bytes: recorte.size }) });
  await enviarVariante(pedido.upload, recorte);
  return pedir<{ url: string }>(`/api/v1/trips/${tripId}/cover/complete`, { method: 'POST', body: JSON.stringify({ key: pedido.key, mimeType: 'image/jpeg' }) });
}

export function useTrocarCapa(tripId: string) {
  const cliente = useQueryClient();
  const depois = () => {
    void cliente.invalidateQueries({ queryKey: ['viagem', 'capa', tripId] });
    void cliente.invalidateQueries({ queryKey: ['viagens'] });
  };
  const trocar = useMutation({ mutationFn: (recorte: Blob) => enviarCapa(tripId, recorte), onSuccess: depois });
  const tirar = useMutation({ mutationFn: () => pedir<{ ok: true }>(`/api/v1/trips/${tripId}/cover`, { method: 'DELETE' }), onSuccess: depois });
  return { trocar, tirar };
}
